import { useReactiveVar } from '@apollo/client'
import { WhatsApp, ChevronLeft, ChevronRight, PhotoAlbumOutlined } from '@mui/icons-material'
import { Box, Button, Collapse, Hidden, LinearProgress, List, Stack, Slide, Typography } from '@mui/material'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { styled, useTheme } from '@mui/material/styles'
import { useFlag } from '@unleash/proxy-client-react'
import { getAuth } from 'firebase/auth'
import { useState } from 'react'
import ReactInfiniteScroll from 'react-infinite-scroll-component'
import { Link } from 'react-router-dom'
import { IntegrationStatus, IntegrationType, UserProfileFragmentFragmentDoc } from '../../../../../graphql/generated'
import usePendoWhatsAppTracking from '../../../../../hooks/usePendoWhatsAppTracking'
import cache, { drawerVar } from '../../../../../providers/apollo/cache'
import useBucket from '../../../hooks/useBucket'
import SearchBar from '../../appbar/SearchBar'
import AccountMenu from '../components/AccountMenu'
import BucketAlbum from '../components/Album'
import AlbumSubHeader from '../components/AlbumSubHeader'
import BottomStaticAlbums from '../components/BottomStaticAlbums'
import BucketAdminActions from '../components/BucketAdminActions'
import BucketMenu from '../components/BucketMenu'
import BucketSwitcher from '../components/BucketSwitcher'
import BucketSwitcherCurrent from '../components/BucketSwitcherCurrent'
import StaticAlbums from '../components/StaticAlbums'

interface Props {
  bucketId: string
  userId: string
}

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  width: '280px',
  '& a': {
    color: 'inherit'
  },
  '& #album-menu': {
    width: '280px'
  }
}))

const formatCount = (count: number = 0) => {
  switch (count) {
    case 0:
      return 'No images'
    case 1:
      return `1 image`
    default:
      return `${count} images`
  }
}

export default function MobileDrawer({ bucketId, userId }: Props) {
  const theme = useTheme()
  const drawer = useReactiveVar(drawerVar)
  const [isOpen, setIsOpen] = useState(true)
  const [drawerSection, setDrawerSection] = useState<'main' | 'albums'>('main')
  const { bucket, bucketAlbums, bucketUser, fetchMore, loading, loadingMore } = useBucket()
  const useWhatsAppIntegration = useFlag('useWhatsAppIntegration')
  const enabledNewBucketSwitcher = useFlag('enabledNewBucketSwitcher')
  const enableSubdrawer = useFlag('enableSubdrawer')
  const { trackWhatsAppEvent } = usePendoWhatsAppTracking()
  const userProfile = cache.readFragment({
    id: `UserProfile:${userId}`,
    fragmentName: 'UserProfileFragment',
    fragment: UserProfileFragmentFragmentDoc
  })
  const hasActiveWhatsAppIntegration = userProfile?.integrations?.some(
    (integration) => integration?.integrationType === IntegrationType.Whatsapp && integration?.status === IntegrationStatus.Active
  )
  const isWhatsAppEnabled = userProfile?.plan?.is_whatsapp_enabled
  const isEmailVerified = getAuth().currentUser?.emailVerified
  const showWhatsAppButton = !bucket?.isBanned && useWhatsAppIntegration && isEmailVerified && isWhatsAppEnabled

  const imgCount = formatCount(bucket?.counters?.totalMediaMinusTrash ?? 0)
  const bucketTitle = bucket?.title
  const albums = bucketAlbums?.items || []
  const nextToken = bucketAlbums?.nextToken
  const permissions = bucketUser?.permissions

  const onClickWhatsApp = () => {
    const buttonText = hasActiveWhatsAppIntegration ? 'Manage WhatsApp' : 'Connect to WhatsApp'
    trackWhatsAppEvent('whatsapp_sidebar_connect_clicked', {
      platform: 'mobile',
      user_has_integration: hasActiveWhatsAppIntegration,
      button_text: buttonText,
      source_page: window.location.pathname,
      bucket_id: bucketId
    })

    let redirectUrl = `/integrations/whatsapp/setup`
    if (hasActiveWhatsAppIntegration) {
      redirectUrl = `/integrations/manage`
    }
    window.location.href = redirectUrl
  }

  const handleOpen = () => drawerVar('main')
  const handleClose = () => drawerVar(null)

  const handleOpenAlbums = () => setDrawerSection('albums')
  const handleBack = () => setDrawerSection('main')

  const toggle = () => setIsOpen(!isOpen)

  const AlbumSection = (
    <Box
      sx={{
        flex: 1,
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: enableSubdrawer ? '0' : '0 8px 16px',
        minWidth: '264px'
      }}
    >
      {!enabledNewBucketSwitcher && (
        <Box sx={{ padding: '0 8px' }}>
          <StaticAlbums />
        </Box>
      )}
      {loading && <LinearProgress color="primary" />}
      <List sx={{ flex: 'auto', padding: '0 8px' }}>
        <ReactInfiniteScroll
          dataLength={albums.length || 0}
          loader={
            <Typography variant="h6" align="center">
              Loading...
            </Typography>
          }
          next={fetchMore}
          hasMore={Boolean(nextToken)}
        >
          {!loading && <AlbumSubHeader isOpen={isOpen} toggle={toggle} bucketId={bucketId} />}
          <Collapse in={isOpen}>
            {albums.map((album) => (
              <BucketAlbum album={album} key={album.id} bucketId={bucketId} />
            ))}
            {Boolean(nextToken) && (
              <Button disabled={loadingMore} onClick={fetchMore} fullWidth>
                load more
              </Button>
            )}
            {enabledNewBucketSwitcher && (
              <Box sx={{ padding: '0 8px' }}>
                <BottomStaticAlbums />
              </Box>
            )}
          </Collapse>
        </ReactInfiniteScroll>
      </List>
    </Box>
  )

  return (
    <StyledSwipeableDrawer
      anchor={theme.direction === 'rtl' ? 'right' : 'left'}
      onClose={handleClose}
      onOpen={handleOpen}
      open={drawer === 'main'}
      variant="temporary"
      swipeAreaWidth={0}
      keepMounted
      PaperProps={{ id: 'album-menu' }}
    >
      {bucketId && (
        <Box sx={{ padding: enableSubdrawer ? 0 : '8px', maxHeight: 'calc(100vh - 65px)', overflowY: 'auto' }}>
          <Box sx={{ padding: '8px 8px 0', maxWidth: '280px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                {enabledNewBucketSwitcher ? (
                  <Button
                    onClick={handleBack}
                    disabled={drawerSection === 'main'}
                    sx={{
                      padding: 0,
                      color: theme.palette.text.primary,
                      ':disabled': { color: theme.palette.text.primary },
                      ':hover': { backgroundColor: 'transparent', color: theme.palette.primary.main }
                    }}
                  >
                    {drawerSection === 'albums' && <ChevronLeft />}
                    <Typography component="h3" sx={{ fontSize: '1.5rem', fontWeight: '600', lineHeight: 1.25 }}>
                      Buckets
                    </Typography>
                  </Button>
                ) : (
                  <>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      {bucket && <BucketAdminActions bucket={bucket} permissions={permissions} />}
                      <Typography component="h3" sx={{ fontSize: '1.25rem', fontWeight: '600', lineHeight: 1.25 }}>
                        {bucketTitle}
                      </Typography>
                    </Stack>
                    {!bucket?.isBanned && <Typography variant="caption">{imgCount}</Typography>}
                  </>
                )}
              </Box>
              {enabledNewBucketSwitcher ? (
                <Link to="/buckets" style={{ textDecoration: 'none', color: theme.palette.secondary.main, fontSize: '.875rem', fontWeight: 500 }}>
                  View All
                </Link>
              ) : (
                <BucketMenu permissions={permissions} bucketId={bucketId} />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              flex: enableSubdrawer ? 1 : 'none',
              position: 'relative',
              overflowY: 'auto',
              overflowX: 'hidden',
              maxWidth: '280px',
              padding: enableSubdrawer ? '0 8px' : 0
            }}
          >
            <Stack
              direction={drawerSection === 'main' ? 'column' : 'row'}
              sx={{ gap: '8px', minHeight: drawerSection === 'main' ? 0 : 'calc(100vh - 168px)' }}
            >
              {enabledNewBucketSwitcher ? (
                <Slide direction="right" appear={enableSubdrawer} in={drawerSection === 'main'} mountOnEnter unmountOnExit>
                  <Box>
                    <BucketSwitcher userId={userId} drawerSection={drawerSection} />
                  </Box>
                </Slide>
              ) : (
                <BucketSwitcherCurrent userId={userId} />
              )}
              {!bucket?.isBanned && drawerSection === 'albums' && enableSubdrawer && (
                <Slide direction="left" in={drawerSection === 'albums'} mountOnEnter unmountOnExit>
                  {AlbumSection}
                </Slide>
              )}
            </Stack>
            {!bucket?.isBanned && (
              <>
                {drawerSection === 'main' && (
                  <Hidden lgUp>
                    <Box sx={{ borderBottom: '1px solid #dfdfdf', borderTop: enableSubdrawer ? 'none' : '1px solid #dfdfdf', padding: '8px 0' }}>
                      <SearchBar />
                    </Box>
                  </Hidden>
                )}
                {!bucket?.isBanned && drawerSection === 'main' && enableSubdrawer && (
                  <Button
                    onClick={handleOpenAlbums}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      color: theme.palette.text.primary,
                      padding: '8px 0',
                      fontSize: '1.125rem'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PhotoAlbumOutlined />
                      {albums.length} Albums
                    </Box>
                    <ChevronRight />
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
      {!bucket?.isBanned && !enableSubdrawer && AlbumSection}
      {drawerSection === 'main' && (
        <Box sx={{ marginTop: 'auto', padding: '0 8px', justifySelf: 'flex-end' }}>
          {showWhatsAppButton && (
            <Box sx={{ borderTop: `1px solid ${theme.palette.customGrey[200]}` }}>
              <Button
                fullWidth
                variant="text"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  alignContent: 'center',
                  color: '#101828',
                  gap: '4px',
                  padding: '8px 0',
                  '&:hover': {
                    background: 'transparent',
                    color: theme.palette.primary.main
                  }
                }}
                onClick={onClickWhatsApp}
              >
                <WhatsApp />
                <Typography sx={{ fontWeight: '500', fontSize: '16px', alignContent: 'center' }}>
                  {hasActiveWhatsAppIntegration ? 'Manage WhatsApp' : 'Connect to WhatsApp'}
                </Typography>
                {!hasActiveWhatsAppIntegration && (
                  <Typography
                    component="span"
                    sx={{ backgroundColor: '#35E8B1', borderRadius: '1px', padding: '1px 4px', fontWeight: '700', fontSize: '10px', ml: '4px' }}
                  >
                    new
                  </Typography>
                )}
              </Button>
            </Box>
          )}
          <AccountMenu />
        </Box>
      )}
    </StyledSwipeableDrawer>
  )
}
